import * as React from 'react';
import * as _ from 'underscore';

import { ButtonWidget, ButtonType } from '@tableau/widgets';

import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { SiteUrlsProps } from '../../selectors/SiteUrlsSelectors';
import { ConnectedSpaceHeader } from '../Header/SpaceHeader';
import { FavoriteToggleWithCountsIcon, ActionsMenuIcon } from '../Header/HeaderNameRowIcons';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { CollectionShareTestId, CollectionPrivateHeader } from './CollectionSpace';
import { Messages } from '../../../codegen/Localize';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { OwnerMetadata, ModifiedDateMetadata, VerticalDivider, DefaultVerticalDividerPadding } from '../Header/HeaderMetadataRow';
import { RadioEditProvider } from '../../../dataRole/utils/RadioEditProvider';
import { EditableCollectionDescriptionInline } from './CollectionHeaderEditableDescription';
import { EditableCollectionName } from './CollectionHeaderEditableName';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { SpaceInfo } from '../../state/SpaceInfo';
import { ActionMenuItemClickedChildFunction, ActionMenuItemClickedTelemetryContext } from '../../contexts/TelemetryContexts';
import { generateTelemetryEventHandler } from '../../../common/telemetryEvents/util/ContentTelemetry';
import { TelemetryEvents, CollectionInlineActionType, CollectionInlineActionEvent } from '../../../common/telemetryEvents/TelemetryEvents';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { ContentLocation } from '../../../common/telemetryEvents/types/ContentTelemetryTypes';
import { Telemetry } from '../../../commonInfra/services/Telemetry';

export const COLLECTION_NAME_ROW_HEIGHT = 42;
const LOCKED_IMG = imgPath('lock_locked_14px-small.svg');

export interface CollectionSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'collection'>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly onShare: () => void;
  readonly isPublicCollectionsEnabled: boolean;
  readonly selectedContentItemsCount: number;
  readonly isAdminOrOwner: boolean;
  readonly isOwner: boolean;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly openDetail: () => void;
  readonly reloadSpace: () => void;
}

export function CollectionSpaceHeader(props: CollectionSpaceHeaderProps): JSX.Element {
  const actionsMenuDisabled = props.selectedContentItemsCount > 0;
  const actionsMenuButtonTitle = actionsMenuDisabled ? Messages.collectionActionsMenuDisabled() : null;
  const canEdit = props.isAdminOrOwner;
  const [ editingNameOrDescription, onNameOrDescEditChange ] = React.useState(false);

  const onEditChange = (isEditing: boolean, actionType: CollectionInlineActionType) => {
    onNameOrDescEditChange(isEditing);

    if (isEditing) {
      Telemetry.sendEvent<CollectionInlineActionEvent>(
        { collection_id: props.item.id, actionType, type: TelemetryEvents.CollectionInlineAction }
      );
    }
  };
  const onNameEdit = (isEditing: boolean) => {
    onEditChange(isEditing, 'rename');
  };
  const onDescriptionEdit = (isEditing: boolean) => {
    onEditChange(isEditing, 'set-description');
  };

  const spaceInfo: SpaceInfo = {spaceType: 'collection', item: props.item, subspaceName: undefined};

  return <RadioEditProvider isReadonly={!canEdit}>
    <ConnectedSpaceHeader
      item={props.item}
      availableActions={undefined}
      containerClassName={SpaceHeaderStyles.CollectionHeaderStyles}
      nameElement={<EditableCollectionName {...props} canEdit={!editingNameOrDescription} onEditChange={onNameEdit}/>}
      hideHeaderIcon={true}
      nameRowIcons={
        <div className={SpaceHeaderStyles.CollectionNameRow}>
          {FeatureFlags.useCollectionsFavorites && FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {
            <ActionMenuItemClickedTelemetryContext.Provider value={generateActionMenuItemClickedTelemetryContext(spaceInfo)}>
              {ActionsMenuIcon(props.item, actionsMenuDisabled, actionsMenuButtonTitle)}
            </ActionMenuItemClickedTelemetryContext.Provider>
          }
          {FeatureFlags.newShareDialogEnabled && FeatureFlags.shareCollectionsEnabled && props.isPublicCollectionsEnabled &&
            <ButtonWidget
              style={{ marginLeft: 'auto' }}
              buttonType={ButtonType.Outline}
              handleClick={props.onShare}
              disabled={false}
              testId={CollectionShareTestId}
            >
              {Messages.shareButtonCollection()}
            </ButtonWidget>
          }
        </div>
      }
      descriptionElement={<EditableCollectionDescriptionInline {...props} canEdit={!editingNameOrDescription} onEditChange={onDescriptionEdit}/>}
      metadataRow={
        <>
          {!_.isUndefined(props.item.owner) && OwnerMetadata(props.item.owner, props.urls)}
          {!_.isUndefined(props.item.owner) && VerticalDivider()}
          {ModifiedDateMetadata(props.item.updatedAt, false)}
          {props.item.visibility === 'private' &&
            <>
              {VerticalDivider(DefaultVerticalDividerPadding, 2)}
              <div data-tb-test-id={CollectionPrivateHeader}>
                <img src={LOCKED_IMG}
                  alt={Messages.collectionsPrivateAlt()}
                />
                <span style={{ marginLeft: 2 }}>{Messages.collectionVisibilityPrivate()}</span>
              </div>
            </>
          }
        </>
      }
    />
  </RadioEditProvider>;
}

export function generateActionMenuItemClickedTelemetryContext(spaceInfo: SpaceInfo) {
  return function ActionMenuItemClickedTelemetryContext(childFunction: ActionMenuItemClickedChildFunction) {
    return (
      childFunction((actionType: string) => {
        const eventHandler = generateTelemetryEventHandler(
          TelemetryEvents.ActionMenuItemClicked,
          spaceInfo,
          [{id: spaceInfo.item.id, contentType: spaceInfo.item.contentType}] as IContentInfo[],
          {id: spaceInfo.item.id, contentType: spaceInfo.item.contentType} as IContentInfo,
          ContentLocation.SpaceHeader,
          null, // content position
          null // ContentTelemetryContextSpecificData
        );
        eventHandler(actionType);
      })
    );
  };
}
